import { useEffect, useState, useRef, useCallback } from "react";
import { Link, NavLink, useLocation, useNavigate, Navigate, useNavigationType } from "react-router-dom";
import {
  LayoutDashboard, Wand2, Network, Mic2, Building2, Wrench, HardHat, Handshake, Store, Ticket,
  Wallet, Activity, LineChart, Bell, Menu, X, ShieldCheck, LogOut, ScanLine, ListOrdered, Settings,
  CalendarDays, Globe2, Sparkles, PanelLeftClose, PanelLeftOpen, ArrowLeft, ArrowRight, ListChecks, Users2,
} from "lucide-react";
import { Logo } from "@/components/PublicNav";
import WorkspaceSelector from "@/components/WorkspaceSelector";
import NotificationPopover from "@/components/NotificationPopover";
import NotificationHistoryModal from "@/components/NotificationHistoryModal";
import ActionCenterModal from "@/components/ActionCenterModal";
import MessagingDrawer from "@/components/MessagingDrawer";
import { TypewriterDisclaimer } from "@/components/TypewriterDisclaimer";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";

const SIDEBAR_COLLAPSED_KEY = "okkax_dashboard_sidebar_collapsed";
const HISTORY_MAX_INDEX_KEY = "okkax_dashboard_history_max_index";

function readStoredBoolean(key) {
  try {
    return window.localStorage.getItem(key) === "true";
  } catch {
    return false;
  }
}

function getHistoryPosition() {
  const entries = window.navigation?.entries?.();
  const currentEntry = window.navigation?.currentEntry;
  if (entries?.length && currentEntry) {
    const index = entries.findIndex((entry) => entry.key === currentEntry.key);
    if (index >= 0) return { index, maxIndex: entries.length - 1 };
  }

  const index = Number.isInteger(window.history.state?.idx) ? window.history.state.idx : 0;
  let maxIndex = index;
  try {
    const storedMax = Number(window.sessionStorage.getItem(HISTORY_MAX_INDEX_KEY));
    if (Number.isInteger(storedMax)) maxIndex = Math.max(index, storedMax);
  } catch {
    // History controls remain safe when storage is unavailable.
  }
  return { index, maxIndex };
}

export const NAV = {
  organizer: [
    ["/app", "Overview", LayoutDashboard],
    ["/app/workflow", "Workflow", ListChecks],
    ["/app/copilot", "Okkax Copilot", Sparkles],
    ["/app/studio", "Event Studio", Wand2],
    ["/app/ticketing", "Ticketing", Ticket],
    ["/app/operations", "Live Operations", ScanLine],
    ["/app/opportunities", "Opportunities & Deals", Handshake],
    ["/app/finance", "Finance & Escrow", Wallet],
    ["/app/settings", "Settings", Settings],
  ],
  promoter: [
    ["/app", "Overview", LayoutDashboard],
    ["/app/workflow", "Workflow", ListChecks],
    ["/app/copilot", "Okkax Copilot", Sparkles],
    ["/app/studio", "Event Studio", Wand2],
    ["/app/ticketing", "Ticketing", Ticket],
    ["/app/operations", "Live Operations", ScanLine],
    ["/app/opportunities", "Opportunities & Deals", Handshake],
    ["/app/finance", "Finance & Settlement", Wallet],
    ["/app/settings", "Settings", Settings],
  ],
  talent: [
    ["/app", "Overview", LayoutDashboard],
    ["/app/workflow", "Workflow", ListChecks],
    ["/app/copilot", "Okkax Copilot", Sparkles],
    ["/app/opportunities", "Booking Requests", Handshake],
    ["/app/me", "My Bookings & Rider", Mic2],
    ["/app/calendar", "Calendar", CalendarDays],
    ["/app/wallet", "Wallet & Payouts", Wallet],
    ["/app/settings", "Settings", Settings],
  ],
  workforce: [
    ["/app", "Overview", LayoutDashboard],
    ["/app/workflow", "Workflow", ListChecks],
    ["/app/copilot", "Okkax Copilot", Sparkles],
    ["/app/jobs", "Available Jobs", HardHat],
    ["/app/me", "My Assignments", ListOrdered],
    ["/app/calendar", "Shifts & Schedule", CalendarDays],
    ["/app/wallet", "Wallet & Earnings", Wallet],
    ["/app/settings", "Settings", Settings],
  ],
  vendor: [
    ["/app", "Overview", LayoutDashboard],
    ["/app/workflow", "Workflow", ListChecks],
    ["/app/copilot", "Okkax Copilot", Sparkles],
    ["/app/opportunities", "RFQ & Requests", Wrench],
    ["/app/me", "Projects & Deliverables", ListOrdered],
    ["/app/network", "Services & Inventory", Globe2],
    ["/app/calendar", "Resource Calendar", CalendarDays],
    ["/app/wallet", "Wallet & Receivables", Wallet],
    ["/app/settings", "Settings", Settings],
  ],
  venue: [
    ["/app", "Overview", LayoutDashboard],
    ["/app/workflow", "Workflow", ListChecks],
    ["/app/copilot", "Okkax Copilot", Sparkles],
    ["/app/opportunities", "Venue Inquiries", Handshake],
    ["/app/me", "Venue & Bookings", Building2],
    ["/app/calendar", "Availability Calendar", CalendarDays],
    ["/app/wallet", "Wallet & Receivables", Wallet],
    ["/app/settings", "Settings", Settings],
  ],
  sponsor: [
    ["/app", "Overview", LayoutDashboard],
    ["/app/workflow", "Workflow", ListChecks],
    ["/app/copilot", "Okkax Copilot", Sparkles],
    ["/app/discover", "Discover Events", Globe2],
    ["/app/sponsor", "Sponsorship Inventory", Handshake],
    ["/app/me", "Activations & Portfolio", Activity],
    ["/app/finance", "Finance & Commitments", Wallet],
    ["/app/settings", "Settings", Settings],
  ],
  tenant: [
    ["/app", "Overview", LayoutDashboard],
    ["/app/workflow", "Workflow", ListChecks],
    ["/app/copilot", "Okkax Copilot", Sparkles],
    ["/app/discover", "Discover Events", Globe2],
    ["/app/tenant", "Applications & Booths", Store],
    ["/app/calendar", "Event Schedule", CalendarDays],
    ["/app/finance", "Payments & Invoices", Wallet],
    ["/app/settings", "Settings", Settings],
  ],
  audience: [
    ["/app", "Overview", LayoutDashboard],
    ["/app/workflow", "Workflow", ListChecks],
    ["/app/discover", "Discover Events", Globe2],
    ["/app/tickets", "My Tickets", Ticket],
    ["/app/orders", "Orders & Payments", Wallet],
    ["/app/settings", "Settings", Settings],
  ],
  admin: [
    ["/app", "Overview", LayoutDashboard],
    ["/app/workflow", "Workflow", ListChecks],
    ["/app/admin", "Admin Panel", ShieldCheck],
    ["/app/admin/control", "Control Plane", Activity],
    ["/app/admin/finance", "Pergerakan Dana", Wallet],
    ["/app/settings", "Settings", Settings],
  ],
};

export const EVENT_TABS = [
  ["blueprint", "Blueprint", Wand2],
  ["graph", "Event Graph", Network],
  ["team", "Team", Users2],
  ["talent", "Talent & Rider", Mic2],
  ["venue", "Venue", Building2],
  ["vendors", "Vendors", Wrench],
  ["workforce", "Workforce", HardHat],
  ["sponsors", "Sponsors", Handshake],
  ["tenants", "Tenants", Store],
  ["tickets", "Tickets", Ticket],
  ["budget", "Budget & Simulator", Wallet],
  ["payments", "Payments", Wallet],
  ["operations", "Operations", Activity],
  ["calendar", "Calendar", CalendarDays],
  ["ripple", "Live Event Impact", LineChart],
];

export function resolveWorkspaceNav(role, accountRoles = []) {
  const currentRole = String(role || "audience").toLowerCase();
  if (["admin", "super_admin", "platform_admin"].includes(currentRole) ||
      accountRoles.some((item) => ["super_admin", "platform_admin"].includes(item))) {
    return NAV.admin;
  }
  if (["organizer", "event_organizer", "supervisor", "finance_approver"].includes(currentRole)) return NAV.organizer;
  if (currentRole === "promoter") return NAV.promoter;
  if (["talent", "talent_management", "artist", "band"].includes(currentRole)) return NAV.talent;
  if (["workforce", "worker", "crew", "freelancer"].includes(currentRole)) return NAV.workforce;
  if (["venue", "venue_manager"].includes(currentRole)) return NAV.venue;
  if (["vendor", "supplier"].includes(currentRole)) return NAV.vendor;
  if (["sponsor", "brand"].includes(currentRole)) return NAV.sponsor;
  if (["tenant", "merchant"].includes(currentRole)) return NAV.tenant;
  return NAV.audience;
}

export default function AppShell({ children }) {
  const { user, org, loading, logout, hasRole, activeWorkspace, effectiveRole, notificationVersion } = useAuth();
  const [open, setOpen] = useState(false);
  const [notif, setNotif] = useState({ items: [], unread: 0, total: 0 });
  const [showNotif, setShowNotif] = useState(false);
  const [showHistory, setShowHistory] = useState(false);
  const [showActionCenter, setShowActionCenter] = useState(false);
  const [actionCount, setActionCount] = useState(0);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(() => readStoredBoolean(SIDEBAR_COLLAPSED_KEY));
  const [historyPosition, setHistoryPosition] = useState(() => getHistoryPosition());
  const notifBtnRef = useRef(null);
  const nav = useNavigate();
  const location = useLocation();
  const navigationType = useNavigationType();

  const userId = user?.id;
  const isAudienceWorkspace = effectiveRole === "audience";
  const fetchNotifs = useCallback(async () => {
    if (!userId) return;
    try {
      const [{ data: notifData }, { data: actionData }] = await Promise.all([
        api.get("/notifications?limit=12").catch(() => ({ data: { items: [], unread: 0, total: 0 } })),
        isAudienceWorkspace
          ? Promise.resolve({ data: { items: [] } })
          : api.get("/overview/actions").catch(() => ({ data: { items: [] } })),
      ]);
      setNotif(notifData || { items: [], unread: 0, total: 0 });
      const pending = (actionData?.items || []).filter(
        (a) => a.status === "pending" || a.status === "action_required"
      ).length;
      setActionCount(pending);
    } catch {
      // Ignore
    }
  }, [isAudienceWorkspace, userId]);

  useEffect(() => {
    setNotif({ items: [], unread: 0, total: 0 });
    setActionCount(0);
    setShowNotif(false);
    fetchNotifs();
  }, [fetchNotifs, notificationVersion]);

  useEffect(() => {
    try {
      window.localStorage.setItem(SIDEBAR_COLLAPSED_KEY, String(sidebarCollapsed));
    } catch {
      // Persistence is optional; the in-memory control still works.
    }
  }, [sidebarCollapsed]);

  useEffect(() => {
    const updateHistoryPosition = () => {
      const next = getHistoryPosition();
      if (!window.navigation?.entries && navigationType === "PUSH") {
        next.maxIndex = next.index;
      }
      try {
        window.sessionStorage.setItem(HISTORY_MAX_INDEX_KEY, String(next.maxIndex));
      } catch {
        // History navigation itself does not depend on storage.
      }
      setHistoryPosition(next);
    };

    updateHistoryPosition();
    window.addEventListener("popstate", updateHistoryPosition);
    window.navigation?.addEventListener?.("currententrychange", updateHistoryPosition);
    return () => {
      window.removeEventListener("popstate", updateHistoryPosition);
      window.navigation?.removeEventListener?.("currententrychange", updateHistoryPosition);
    };
  }, [location.key, navigationType]);

  if (loading) return <div className="p-10 text-sm text-zinc-500">Memuat Okkax…</div>;
  if (!user) return <Navigate to="/login" replace />;

  const currentRole = (activeWorkspace?.role || user?.role || (user?.roles && user.roles[0]) || "audience").toLowerCase();

  const audiencePathAllowed =
    [
      "/app",
      "/app/discover",
      "/app/tickets",
      "/app/orders",
      "/app/settings",
      "/app/payment/success",
      "/app/payment/cancel",
    ].includes(location.pathname) ||
    /^\/app\/discover\/events\/[^/]+\/?$/.test(location.pathname) ||
    /^\/app\/checkout\/[^/]+\/[^/]+\/?$/.test(location.pathname);

  if (isAudienceWorkspace && !audiencePathAllowed) {
    return <Navigate to="/app" replace />;
  }

  const links = resolveWorkspaceNav(currentRole, user.roles || []);

  const handleMarkAllRead = async () => {
    try {
      await api.post("/notifications/read");
      setNotif((prev) => ({
        ...prev,
        unread: 0,
        items: (prev.items || []).map((i) => ({ ...i, read: true })),
      }));
    } catch {
      // Ignore
    }
  };

  const handleMarkSingleRead = async (id) => {
    try {
      await api.post(`/notifications/${id}/read`);
      setNotif((prev) => ({
        ...prev,
        unread: Math.max(0, prev.unread - 1),
        items: (prev.items || []).map((i) =>
          i.id === id ? { ...i, read: true } : i
        ),
      }));
    } catch {
      // Ignore
    }
  };

  const formatUnreadBadge = (count) => {
    if (!count || count <= 0) return null;
    if (count >= 100) return "99+";
    return count.toString();
  };

  const SidebarLinks = ({ onClick }) => (
    <nav className="flex flex-col gap-0.5 px-1.5 font-gemini">
      {links.map(([to, label, Icon]) => (
        <NavLink
          key={to + label}
          to={to}
          end={to === "/app" || to === "/app/admin"}
          onClick={onClick}
          data-testid={`side-${label.toLowerCase().replace(/[^a-z]+/g, "-")}`}
          className={({ isActive }) =>
            `flex items-center gap-2 rounded-lg px-2.5 py-1.5 text-xs font-semibold transition-all ${
              isActive
                ? "bg-white/[0.1] text-white shadow-sm border border-white/[0.08]"
                : "text-zinc-400 hover:bg-white/[0.04] hover:text-white"
            }`
          }
        >
          <Icon size={14.5} className="shrink-0" /> <span>{label}</span>
        </NavLink>
      ))}
    </nav>
  );

  return (
    <div className="flex h-[100dvh] min-h-0 flex-col overflow-hidden bg-[#07070a] font-gemini">
      <header className="fixed top-0 left-0 right-0 z-50 border-b border-white/[0.08] bg-[#06060a]/90 backdrop-blur-2xl shadow-[0_4px_24px_rgba(0,0,0,0.6)]">
        <div className="flex items-center justify-between px-3.5 py-2 sm:px-5">
          <div className="flex min-w-0 items-center gap-2.5">
            <button data-testid="shell-drawer-toggle" onClick={() => setOpen(true)} className="p-1.5 text-zinc-300 lg:hidden rounded-lg hover:bg-white/[0.05]" aria-label="Buka menu">
              <Menu size={18} />
            </button>
            <div className="hidden items-center gap-1 lg:flex" aria-label="Navigasi dashboard">
              <button
                data-testid="shell-sidebar-toggle"
                onClick={() => setSidebarCollapsed((value) => !value)}
                className="rounded-lg border border-white/[0.08] bg-white/[0.03] p-1.5 text-zinc-300 transition-all hover:border-white/20 hover:bg-white/[0.07] hover:text-white disabled:cursor-not-allowed disabled:opacity-35"
                aria-label={sidebarCollapsed ? "Tampilkan sidebar dashboard" : "Sembunyikan sidebar dashboard"}
                title={sidebarCollapsed ? "Tampilkan sidebar" : "Sembunyikan sidebar"}
              >
                {sidebarCollapsed ? <PanelLeftOpen size={14.5} /> : <PanelLeftClose size={14.5} />}
              </button>
              <button
                data-testid="shell-history-back"
                onClick={() => nav(-1)}
                disabled={historyPosition.index <= 0}
                className="rounded-lg border border-white/[0.08] bg-white/[0.03] p-1.5 text-zinc-300 transition-all hover:border-white/20 hover:bg-white/[0.07] hover:text-white disabled:cursor-not-allowed disabled:opacity-35 disabled:hover:border-white/[0.08] disabled:hover:bg-white/[0.03]"
                aria-label="Kembali"
                title="Kembali"
              >
                <ArrowLeft size={14.5} />
              </button>
              <button
                data-testid="shell-history-forward"
                onClick={() => nav(1)}
                disabled={historyPosition.index >= historyPosition.maxIndex}
                className="rounded-lg border border-white/[0.08] bg-white/[0.03] p-1.5 text-zinc-300 transition-all hover:border-white/20 hover:bg-white/[0.07] hover:text-white disabled:cursor-not-allowed disabled:opacity-35 disabled:hover:border-white/[0.08] disabled:hover:bg-white/[0.03]"
                aria-label="Maju"
                title="Maju"
              >
                <ArrowRight size={14.5} />
              </button>
            </div>
          </div>
          <div className="pointer-events-none absolute left-1/2 top-1/2 flex -translate-x-1/2 -translate-y-1/2 items-center gap-3">
            <div className="pointer-events-auto">
              <Logo small to="/" ariaLabel="Kembali ke Okkax" />
            </div>
            <span className="hidden whitespace-nowrap text-[11px] text-zinc-400 font-gemini-mono md:inline">Live Event Operating Network</span>
          </div>
          <div className="flex items-center gap-2">
            {/* Permission-Gated Professional Messaging */}
            {!isAudienceWorkspace && <MessagingDrawer />}

            {/* Universal Action Center Trigger */}
            {!isAudienceWorkspace && <div className="relative">
              <button
                data-testid="action-center-btn"
                onClick={() => setShowActionCenter(true)}
                className={`relative p-1.5 rounded-lg border transition-all cursor-pointer ${
                  showActionCenter
                    ? "border-white/40 bg-white/15 text-white shadow-sm"
                    : "border-white/[0.08] bg-white/[0.03] text-zinc-300 hover:border-white/20 hover:text-white"
                }`}
                aria-label="Universal Action Center"
                title="Universal Action Center"
              >
                <Sparkles size={14.5} />
                {actionCount > 0 && (
                  <span
                    data-testid="action-center-badge"
                    className="num absolute -right-1 -top-1 flex h-4 min-w-4 items-center justify-center rounded-full bg-amber-400 px-1 text-[9px] font-extrabold text-black shadow-sm font-gemini-mono"
                  >
                    {actionCount}
                  </span>
                )}
              </button>
            </div>}

            {!isAudienceWorkspace && <ActionCenterModal
              isOpen={showActionCenter}
              onClose={() => {
                setShowActionCenter(false);
                fetchNotifs();
              }}
            />}

            {/* Notification Bell Trigger */}
            <div className="relative">
              <button
                ref={notifBtnRef}
                data-testid="notif-btn"
                onClick={() => setShowNotif((prev) => !prev)}
                className={`relative p-1.5 rounded-lg border transition-all cursor-pointer ${
                  showNotif
                    ? "border-white/40 bg-white/15 text-white shadow-sm"
                    : "border-white/[0.08] bg-white/[0.03] text-zinc-300 hover:border-white/20 hover:text-white"
                }`}
                aria-label="Notifikasi"
              >
                <Bell size={14.5} />
                {notif.unread > 0 && (
                  <span
                    data-testid="notif-badge"
                    className="num absolute -right-1 -top-1 flex h-4 min-w-4 items-center justify-center rounded-full bg-white px-1 text-[9px] font-extrabold text-black shadow-sm font-gemini-mono"
                  >
                    {formatUnreadBadge(notif.unread)}
                  </span>
                )}
              </button>

              <NotificationPopover
                isOpen={showNotif}
                onClose={() => setShowNotif(false)}
                triggerRef={notifBtnRef}
                notifications={notif.items || []}
                unreadCount={notif.unread || 0}
                totalCount={notif.total || 0}
                onMarkAllRead={handleMarkAllRead}
                onMarkSingleRead={handleMarkSingleRead}
                onOpenHistory={() => setShowHistory(true)}
              />
            </div>

            <NotificationHistoryModal
              isOpen={showHistory}
              onClose={() => {
                setShowHistory(false);
                fetchNotifs();
              }}
              onMarkAllRead={handleMarkAllRead}
              onMarkSingleRead={handleMarkSingleRead}
            />

            <button
              data-testid="shell-logout-btn"
              onClick={() => {
                logout();
                nav("/");
              }}
              className="rounded-lg border border-white/[0.08] bg-white/[0.03] p-1.5 text-zinc-300 hover:border-white/20 hover:text-white transition-all cursor-pointer"
              aria-label="Keluar"
            >
              <LogOut size={14.5} />
            </button>
          </div>
        </div>
      </header>

      <div className="flex min-h-0 flex-1 overflow-hidden pt-[56px]">
        <aside
          data-testid="shell-sidebar"
          data-collapsed={sidebarCollapsed ? "true" : "false"}
          className={`hidden h-full shrink-0 overflow-hidden border-r border-white/[0.08] bg-[#060609]/70 transition-[width] duration-200 lg:flex lg:flex-col ${sidebarCollapsed ? "w-0 border-r-0 p-0" : "w-56"}`}
          aria-hidden={sidebarCollapsed}
        >
          {!sidebarCollapsed && (
            <>
              <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain py-3.5">
                <SidebarLinks />
                <div className="mt-6 px-3 text-[10.5px] leading-relaxed text-zinc-400 font-gemini-mono">
                  <TypewriterDisclaimer speed={12} />
                </div>
              </div>
              <div className="shrink-0 border-t border-white/[0.08] p-2.5">
                <WorkspaceSelector placement="sidebar" />
              </div>
            </>
          )}
        </aside>

        {open && (
          <div className="fixed inset-0 z-50 lg:hidden font-gemini">
            <div className="absolute inset-0 bg-black/80 backdrop-blur-sm" onClick={() => setOpen(false)} />
            <div className="absolute left-0 top-0 flex h-full w-64 min-h-0 flex-col overflow-hidden border-r border-white/[0.1] bg-[#09090f] p-4 shadow-2xl">
              <div className="mb-4 flex items-center justify-between">
                <Logo small to="/app" />
                <button onClick={() => setOpen(false)} aria-label="Tutup menu" className="p-1 text-zinc-400 hover:text-white"><X size={16} /></button>
              </div>
              <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain">
                <SidebarLinks onClick={() => setOpen(false)} />
                <div className="mt-4 px-3 text-[10.5px] leading-relaxed text-zinc-400 font-gemini-mono">
                  <TypewriterDisclaimer speed={12} />
                </div>
              </div>
              <div className="mt-3 shrink-0 border-t border-white/[0.08] pt-3">
                <WorkspaceSelector placement="sidebar" />
              </div>
            </div>
          </div>
        )}

        <main
          className="okx-scroll-pane min-h-0 min-w-0 flex-1 overflow-x-hidden overflow-y-auto px-3.5 py-4 pb-16 sm:px-5 sm:py-4.5"
        >
          {children}
        </main>
      </div>
    </div>
  );
}
